// File: app/src/apis/llm/usage.js
//
// Token/cost accounting for a generation (issue #71).
//
// A single "generate" can fan out into several provider calls — a truncation
// retry escalates tokens and runs again, and a blocking validation failure
// triggers one refinement pass. Each call reports one entry here and the
// caller accumulates, so the figure shown to the user is what the whole
// generation cost, not just its last request.
//
// Deliberately pure and dependency-light: it imports only the provider config,
// never `providers.js` or `geminiApi`. The Gemini SDK is ESM-only and Jest
// cannot transform it, so anything importing it is untestable — keeping the
// arithmetic here means it can be tested directly.

import { estimateCost, getCostReporting } from "../../config/llmProviders";
import { formatUsd } from "../../utils/money";

const toCount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
};

const toCost = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

// Pull `{ inputTokens, outputTokens, actualCost }` out of whatever shape the
// provider happens to use. Every cloud provider reports tokens; only
// OpenRouter reports money.
export const extractUsage = (provider, model, payload) => {
  if (!payload || typeof payload !== "object") return null;

  const usage = payload.usage || {};
  let inputTokens = 0;
  let outputTokens = 0;
  let actualCost = null;

  switch (provider) {
    case "openrouter":
      inputTokens = toCount(usage.prompt_tokens);
      outputTokens = toCount(usage.completion_tokens);
      // The one real money figure available to us. `cost` is what OpenRouter
      // charged the user's credits for this request, in USD.
      actualCost = toCost(usage.cost);
      break;

    case "openai":
    case "deepseek":
    case "groq":
      inputTokens = toCount(usage.prompt_tokens);
      outputTokens = toCount(usage.completion_tokens);
      break;

    case "anthropic":
    case "minimax":
      // Anthropic Messages API. Cache reads/writes bill at different rates;
      // folding them into plain input keeps the estimate conservative.
      inputTokens =
        toCount(usage.input_tokens) +
        toCount(usage.cache_read_input_tokens) +
        toCount(usage.cache_creation_input_tokens);
      outputTokens = toCount(usage.output_tokens);
      break;

    case "google": {
      const meta = payload.usageMetadata || {};
      inputTokens = toCount(meta.promptTokenCount);
      // Thinking tokens bill as output, so they belong in the output count.
      outputTokens =
        toCount(meta.candidatesTokenCount) + toCount(meta.thoughtsTokenCount);
      break;
    }

    case "ollama":
      inputTokens = toCount(payload.prompt_eval_count);
      outputTokens = toCount(payload.eval_count);
      actualCost = 0; // Local inference is free, and that is a real figure.
      break;

    default:
      return null;
  }

  if (!inputTokens && !outputTokens && actualCost === null) return null;

  return { provider, model, inputTokens, outputTokens, actualCost };
};

// Resolve one raw entry into a costed entry. Uses the provider's reported cost
// when there is one, otherwise prices the tokens off the rate table.
export const costUsageEntry = (entry) => {
  if (!entry) return null;

  const { provider, model, inputTokens, outputTokens, actualCost } = entry;
  const isActual = actualCost !== null && actualCost !== undefined;
  const estimated = isActual
    ? null
    : estimateCost(provider, model, inputTokens, outputTokens);

  return {
    provider,
    model,
    inputTokens,
    outputTokens,
    cost: isActual ? actualCost : (estimated?.totalCost ?? null),
    isActual,
  };
};

// Fold a generation's calls into one total. `isActual` is true only when every
// call reported real money — one estimated call makes the whole total an
// estimate, and the UI must say so.
export const accumulateUsage = (entries = []) => {
  const costed = entries.map(costUsageEntry).filter(Boolean);

  if (costed.length === 0) return null;

  const totals = costed.reduce(
    (acc, entry) => ({
      inputTokens: acc.inputTokens + entry.inputTokens,
      outputTokens: acc.outputTokens + entry.outputTokens,
      cost: entry.cost === null ? acc.cost : (acc.cost ?? 0) + entry.cost,
    }),
    { inputTokens: 0, outputTokens: 0, cost: null },
  );

  const last = costed[costed.length - 1];

  return {
    provider: last.provider,
    model: last.model,
    calls: costed.length,
    inputTokens: totals.inputTokens,
    outputTokens: totals.outputTokens,
    totalTokens: totals.inputTokens + totals.outputTokens,
    cost: totals.cost,
    // Every call priced, and every one of them from the provider itself.
    isActual: costed.every((entry) => entry.isActual && entry.cost !== null),
    reporting: getCostReporting(last.provider),
  };
};

// Money for humans. Sub-cent generations are the norm here, so a flat 2dp
// would render almost everything as "$0.00". With `money` (utils/money.js)
// it's in the viewer's A$ or US$.
export const formatCost = (cost, money) => {
  if (cost === null || cost === undefined || !Number.isFinite(cost)) return null;
  if (money) return formatUsd(cost, money, { precise: true });
  if (cost === 0) return "$0.00";
  if (cost < 0.01) return `$${cost.toFixed(4)}`;
  if (cost < 1) return `$${cost.toFixed(3)}`;
  return `$${cost.toFixed(2)}`;
};

export const formatTokens = (tokens) => {
  if (!Number.isFinite(tokens) || tokens <= 0) return "0";
  if (tokens < 1000) return String(tokens);
  return `${(tokens / 1000).toFixed(1)}k`;
};

// The one-line summary the UI shows after a generation, e.g.
// "$0.0021 · 8.4k tokens" or "~$0.0021 (est.) · 8.4k tokens · 2 calls".
export const formatUsageSummary = (usage, money) => {
  if (!usage) return null;

  const parts = [];
  const cost = formatCost(usage.cost, money);

  if (usage.reporting === "free") {
    parts.push("Free (local)");
  } else if (cost) {
    parts.push(usage.isActual ? cost : `~${cost} (est.)`);
  }

  if (usage.totalTokens > 0) {
    parts.push(`${formatTokens(usage.totalTokens)} tokens`);
  }

  if (usage.calls > 1) {
    parts.push(`${usage.calls} calls`);
  }

  return parts.length > 0 ? parts.join(" · ") : null;
};
